<script setup>
import { computed, getCurrentInstance, nextTick, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { conditionMessages } from './conditions';

const props = defineProps({
  catalog: { type: Object, required: true },
  context: { type: String, default: 'appointment' },
  field: { type: String, default: '' },
});
const emit = defineEmits(['select']);
const { t } = useI18n({ useScope: 'local', messages: conditionMessages });
const root = ref(null);
const open = ref(false);
const query = ref('');
const expanded = ref('');
const listId = `condition-parameters-${getCurrentInstance().uid}`;
const selected = computed(() =>
  (props.catalog.condition_fields?.[props.context] || []).find(
    field => field.name === props.field
  )
);
const label = computed(() => {
  if (!props.field) return t('conditionEditor.chooseParameter');
  const fieldLabel =
    selected.value?.editor_visible === false || !selected.value?.label
      ? t('conditionEditor.legacyField')
      : selected.value.label;
  return `${t(`conditionEditor.${props.context}`)} → ${fieldLabel}`;
});
const groups = computed(() => {
  const term = query.value.trim().toLocaleLowerCase();
  return (props.catalog.condition_contexts || [])
    .map(context => ({
      context,
      label: t(`conditionEditor.${context}`),
      fields: (props.catalog.condition_fields?.[context] || []).filter(
        field =>
          field.editor_visible !== false &&
          field.label &&
          `${t(`conditionEditor.${context}`)} ${field.label}`
            .toLocaleLowerCase()
            .includes(term)
      ),
    }))
    .filter(group => group.fields.length);
});
const close = () => {
  open.value = false;
  root.value?.querySelector('button')?.focus();
};
const toggle = async () => {
  if (open.value) {
    close();
    return;
  }
  query.value = '';
  expanded.value = props.field ? props.context : '';
  open.value = true;
  await nextTick();
  root.value?.querySelector('input')?.focus();
};
const select = (context, field) => {
  emit('select', { context, field });
  close();
};
const escape = event => {
  if (!open.value) return;
  event.stopPropagation();
  event.preventDefault();
  close();
};
onClickOutside(root, () => {
  open.value = false;
});
</script>

<template>
  <div ref="root" class="min-w-0 space-y-2" @keydown.esc="escape">
    <span :id="`${listId}-label`" class="block text-xs text-n-slate-11">
      {{ t('conditionEditor.chooseParameter') }}
    </span>
    <Button
      type="button"
      color="slate"
      variant="outline"
      class="w-full justify-between text-start"
      :label="label"
      :aria-expanded="open"
      :aria-controls="listId"
      icon="i-lucide-chevron-down"
      trailing-icon
      @click="toggle"
    />
    <div
      v-if="open"
      :id="listId"
      :aria-labelledby="`${listId}-label`"
      role="group"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-2 space-y-2"
    >
      <Input
        v-model="query"
        type="search"
        :placeholder="t('conditionEditor.searchParameters')"
        :aria-label="t('conditionEditor.searchParameters')"
      />
      <div class="max-h-60 overflow-y-auto overscroll-contain">
        <div v-for="group in groups" :key="group.context">
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-expanded="!!query.trim() || expanded === group.context"
            :aria-controls="`${listId}-${group.context}`"
            @click="expanded = expanded === group.context ? '' : group.context"
          >
            <span
              class="size-4 shrink-0"
              :class="
                query.trim() || expanded === group.context
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              aria-hidden="true"
            />
            {{ group.label }}
          </button>
          <div
            v-if="query.trim() || expanded === group.context"
            :id="`${listId}-${group.context}`"
            class="ml-5 border-l border-n-weak pl-2"
          >
            <button
              v-for="item in group.fields"
              :key="item.name"
              type="button"
              class="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="{
                'bg-n-alpha-2':
                  context === group.context && field === item.name,
              }"
              @click="select(group.context, item)"
            >
              {{ item.label }}
              <span
                v-if="context === group.context && field === item.name"
                class="i-lucide-check size-4 shrink-0 text-n-blue-11"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <p v-if="!groups.length" class="m-0 px-3 py-2 text-sm text-n-slate-11">
          {{ t('conditionEditor.noParameters') }}
        </p>
      </div>
    </div>
  </div>
</template>
